import { call, put, takeEvery, select } from 'redux-saga/effects';
import { getType } from 'typesafe-actions';

import { RootState } from '../reducers/rootReducer';
import { updatePickTour, setEditOrderClicked } from '../actions/activePickTour.actions';
import { setLoading } from '../actions/loading.actions';
import { setPickTourPage } from '../actions/pickTourPage.actions';
import { setErrorFallbackPageData } from '../actions/errorFallbackPage.actions';

import { mapPickTourDataToUpdatePickTourRequest } from './updatePickTour.mappers';
import { callAPI } from '../../utils';
import { handleApiErrors, catchApiErrors } from './utils';

import {
  SAGA_UPDATE_PICK_TOUR,
  BFF_V1,
  BFF_PICK_TOURS,
  PICK_TOUR_PAGE_COMPLETE,
  PICK_TOUR_PAGE_EXIT_REVIEW,
  PICK_TOUR_PAGE_PICK_TOUR,
  APP_PAGE_ORDER_LIST,
  APP_PAGE_ERROR_FALLBACK,
  PICK_TOUR_SUB_STATUS_PROGRESS,
  PICKING_APIGEE_BASE_PATH,
  SKIP_ITEMS_ACTION_EDIT_ORDER,
} from '../../constants';

import { ConfigAuthValues } from '../../types';
import { UpdatePickTourRequest } from './types';
import { ActivePickTourState } from '../reducers/types';
import { setAppPage } from '../actions/appPage.actions';

import { ActionTypes as MessagesActionTypes, Messages } from '../reducers/message.reducer';

const callUpdatePickTour = (data: UpdatePickTourRequest, configAuthValues: ConfigAuthValues) => {
  return callAPI({
    method: 'PUT',
    url: `${configAuthValues.APIGEE_BASE_URL}${PICKING_APIGEE_BASE_PATH}${BFF_V1}${BFF_PICK_TOURS}/${data.pickTourID}`,
    data,
  });
};

export function* runUpdatePickTourSaga(action: ReturnType<typeof updatePickTour>) {
  const configAuthValues = yield select((state: RootState) => state.config.authValues);
  const editOrderEnabled = yield select((state: RootState) => state.config.editOrderEnabled);

  const activePickTourFromState: ActivePickTourState = yield select((state: RootState) => state.activePickTour);
  if (activePickTourFromState) {
    yield put(setLoading({ sagaName: SAGA_UPDATE_PICK_TOUR, isLoading: true }));

    const { pickTourType } = mapPickTourDataToUpdatePickTourRequest(action.payload, activePickTourFromState.data);

    try {
      const result = yield call(
        callUpdatePickTour,
        mapPickTourDataToUpdatePickTourRequest(action.payload, activePickTourFromState.data),
        configAuthValues
      );
      if (action.payload.updateType === 'finish') {
        if (pickTourType === 'RECALL')
          yield put({ type: MessagesActionTypes.SEND_MESSAGE, payload: Messages.PRINT_RECALL_REPORT });
        if (pickTourType === 'OVERSTOCK')
          yield put({ type: MessagesActionTypes.SEND_MESSAGE, payload: Messages.PRINT_DISCREPANCY_REPORT });
        yield put(
          setEditOrderClicked({
            clicked: editOrderEnabled && action.payload.skipItemsAction === SKIP_ITEMS_ACTION_EDIT_ORDER,
          })
        );
        yield put(setPickTourPage({ name: PICK_TOUR_PAGE_COMPLETE }));
      }
      if (action.payload.updateType === 'inactive') {
        yield put(setAppPage({ name: APP_PAGE_ORDER_LIST }));
        yield put(setPickTourPage({ name: PICK_TOUR_PAGE_PICK_TOUR }));
      }
      if (action.payload.updateType === 'exit') {
        yield put(setPickTourPage({ name: PICK_TOUR_PAGE_EXIT_REVIEW }));
      }
      yield handleApiErrors(result?.data?.errors);
    } catch (error) {
      // 404/400 with ORDER CLOSED detail for finish is not considered an error, should proceed as successful
      if (
        action.payload.updateType === 'finish' &&
        error?.response?.data?.errors &&
        [404, 400].includes(error?.response?.status) &&
        error?.response?.data?.errors?.some((e: { detail: string }) =>
          ['ORDER_CLOSED', 'PICKTOUR_CLOSED', 'PICKTOUR_ACTIVE'].includes(e.detail)
        )
      ) {
        if (pickTourType === 'RECALL')
          yield put({ type: MessagesActionTypes.SEND_MESSAGE, payload: Messages.PRINT_RECALL_REPORT });
        if (pickTourType === 'OVERSTOCK')
          yield put({ type: MessagesActionTypes.SEND_MESSAGE, payload: Messages.PRINT_DISCREPANCY_REPORT });
        yield put(
          setEditOrderClicked({
            clicked: editOrderEnabled && action.payload.skipItemsAction === SKIP_ITEMS_ACTION_EDIT_ORDER,
          })
        );
        yield put(setPickTourPage({ name: PICK_TOUR_PAGE_COMPLETE }));
        yield put(setLoading({ sagaName: SAGA_UPDATE_PICK_TOUR, isLoading: false }));
        return;
      }

      // If finish tour fails (other errors), navigate to error fallback page
      if (action.payload.updateType === 'finish') {
        yield put(
          setErrorFallbackPageData({
            errorMessage: 'ERRORS.UNABLE_TO_COMPLETE_PICK_TOUR',
            showIcon: true,
            iconType: 'warning',
          })
        );
        yield put(setAppPage({ name: APP_PAGE_ERROR_FALLBACK }));
        yield put(setLoading({ sagaName: SAGA_UPDATE_PICK_TOUR, isLoading: false }));
        return;
      }

      // If exit tour fails, still navigate to pending orders (escape route)
      if (action.payload.updateType === 'exit') {
        yield put(setAppPage({ name: APP_PAGE_ORDER_LIST }));
        yield put(setPickTourPage({ name: PICK_TOUR_PAGE_PICK_TOUR }));
      }

      if (action.payload.updateStatus.pickTourSubStatus === PICK_TOUR_SUB_STATUS_PROGRESS) {
        yield put(setAppPage({ name: APP_PAGE_ORDER_LIST }));
      }
      // 404/400 with ORDER CLOSED detail for other updateTypes is not considered an error
      if (error?.response?.data?.errors && [404, 400].includes(error?.response?.status)) {
        try {
          // Note: This block is only reached if updateType is NOT 'finish' (early return above)
          // The ORDER_CLOSED check is kept for other update types that might encounter it
          if (
            error?.response?.data?.errors?.some((e: { detail: string }) =>
              ['ORDER_CLOSED', 'PICKTOUR_CLOSED', 'PICKTOUR_ACTIVE'].includes(e.detail)
            )
          ) {
            if (pickTourType === 'RECALL')
              yield put({ type: MessagesActionTypes.SEND_MESSAGE, payload: Messages.PRINT_RECALL_REPORT });
            if (pickTourType === 'OVERSTOCK')
              yield put({ type: MessagesActionTypes.SEND_MESSAGE, payload: Messages.PRINT_DISCREPANCY_REPORT });
            yield put(
              setEditOrderClicked({
                clicked: editOrderEnabled && action.payload.skipItemsAction === SKIP_ITEMS_ACTION_EDIT_ORDER,
              })
            );
            yield put(setPickTourPage({ name: PICK_TOUR_PAGE_COMPLETE }));
          } else {
            try {
              yield catchApiErrors(error);
            } catch (_error) {
              yield catchApiErrors(error);
            }
          }
          // eslint-disable-next-line no-empty
        } catch (_error) {}
      } else {
        try {
          const data = mapPickTourDataToUpdatePickTourRequest(action.payload, activePickTourFromState.data);
          const url = `${PICKING_APIGEE_BASE_PATH}${BFF_V1}${BFF_PICK_TOURS}/${data.pickTourID}`;
          const method = 'PUT';
          const config = configAuthValues;
          yield catchApiErrors(error, { url, method, data, config, saga: SAGA_UPDATE_PICK_TOUR });
        } catch (_error) {
          yield catchApiErrors(error);
        }
      }
    }

    yield put(setLoading({ sagaName: SAGA_UPDATE_PICK_TOUR, isLoading: false }));
  }
}

export default function* watchUpdatePickTour() {
  yield takeEvery(getType(updatePickTour), runUpdatePickTourSaga);
}
